import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { useSpace } from "../../../hooks/useSpace";
import { SubjectTaskDatabase } from "../../../types";
import { keys } from "./keys";

type UpdateParams = {
  id: number;
  title?: string;
  state?: 'pending' | 'completed';
  completed_at?: string | null;
};

export function useUpdateSubjectTask(subjectId: string) {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateParams) => {
      const { data, error } = await apiClient
        .from('subject_tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async (newValues) => {
      await queryClient.cancelQueries({ queryKey: keys.getSubjectTasks(subjectId) });
      const previousTasks = queryClient.getQueryData<SubjectTaskDatabase[]>(keys.getSubjectTasks(subjectId));

      queryClient.setQueryData(keys.getSubjectTasks(subjectId), (old: SubjectTaskDatabase[] | undefined) =>
        old?.map((t) => (t.id === newValues.id ? { ...t, ...newValues } : t))
      );

      return { previousTasks };
    },
    onError: (_err, _newValues, context) => {
      queryClient.setQueryData(keys.getSubjectTasks(subjectId), context?.previousTasks);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.getSubjectTasks(subjectId) });
      if (currentSpace?.id) {
        queryClient.invalidateQueries({ queryKey: keys.pendingSubTopics(currentSpace.id) });
        queryClient.invalidateQueries({ queryKey: keys.openTasks(currentSpace.id) });
      }
    },
  });
}
